/**
 * S7's contribution to S2's storage tree-extension registry (plan Task 7.13): the File Shares folder (Create share…,
 * Open share by name…), a share (Snapshots…, Quota…, Properties, Get SAS…, Manage access policies…, Copy mount
 * command…, Delete share…, and Remove from this connection on a named share), a snapshot share (Properties, Delete
 * snapshot…, Get SAS…). Every item is gated through `fileActionReason` (read-only → §3.7 attachment rule → the gate)
 * and carries its refusal as the tooltip. Actions open the share dialogs through `openShareDialog`, the SAS / policies
 * dialogs through the S3 dialog store; this module renders no dialog. Dialogs fire their own outcome events.
 */
import type { ContextMenuAction } from '../../../../explorer/models/TreeModel';
import type { DirectoryRef, FileRef, FileShareOperation, ShareRef, StorageService } from '../../../../../types/azureStorage';
import type { StorageMenuNode } from '../../../../database-objects/services/storageContextMenuService';
import { registerStorageTreeExtension, type StorageServiceFolderAction } from '../../../../database-objects/services/storageTreeExtensions';
import { getConnectionById } from '../../../../connections/utils/connectionLookup';
import { toast } from '../../../../../components/ui/toaster';
import { useStorageDialogStore } from '../../properties/dialogs/storageDialogStore';
import { sasTargetProblem } from '../../properties/sasTargets';
import { openShareDialog, type ShareDialogKind } from '../fileDialogStore';
import { fileActionReason } from '../fileGates';
import { removeNamedShare } from '../namedShareActions';

export const S7_CREATE_SHARE = 's7-create-share';
export const S7_OPEN_SHARE_BY_NAME = 's7-open-share-by-name';
const SHARE_ACTIONS = {
  's7-share-snapshots': { label: 'Snapshots…', icon: 'camera', op: 'snapshot-list', dialog: 'share-snapshots' },
  's7-share-quota': { label: 'Quota…', icon: 'gauge', op: 'set-quota', dialog: 'share-quota' },
  's7-share-properties': { label: 'Properties', icon: 'info', op: 'share-properties', dialog: 'properties' },
  's7-share-sas': { label: 'Get SAS…', icon: 'key', op: 'service-sas', dialog: 'sas' },
  's7-share-policies': { label: 'Manage access policies…', icon: 'lock', op: 'access-policies', dialog: 'access-policies' },
  's7-share-mount': { label: 'Copy mount command…', icon: 'terminal', op: 'mount-command', dialog: 'mount-command' },
  's7-delete-share': { label: 'Delete share…', icon: 'trash-2', op: 'delete-share', dialog: 'delete-share' },
  's7-delete-snapshot': { label: 'Delete snapshot…', icon: 'trash-2', op: 'snapshot-delete', dialog: 'delete-snapshot' },
} as const satisfies Record<string, { label: string; icon: string; op: FileShareOperation; dialog: ShareDialogKind | 'sas' | 'access-policies' }>;
type ShareActionId = keyof typeof SHARE_ACTIONS;
export const S7_REMOVE_NAMED_SHARE = 's7-remove-named-share';

/**
 * C2 N16 (U16 remainder): grouped like the share tab's menus — the share's own settings, then access, then the
 * destructive action, which the tree menu renders red and moves to the end of the whole menu (`orderDestructiveLast`).
 * `null` = a separator between groups.
 */
const LIVE_SHARE_ITEMS: ReadonlyArray<ShareActionId | null> = ['s7-share-properties', 's7-share-snapshots', 's7-share-quota', null, 's7-share-sas', 's7-share-policies', 's7-share-mount', null, 's7-delete-share'];
const SNAPSHOT_SHARE_ITEMS: ReadonlyArray<ShareActionId | null> = ['s7-share-properties', 's7-share-sas', null, 's7-delete-snapshot'];
/** The single rooted node of a folder- or file-scoped attachment: snapshots (refused with the §3.7 sentence) and its properties. */
const SCOPED_ROOT_ITEMS: ReadonlyArray<ShareActionId | null> = ['s7-share-properties', 's7-share-snapshots'];

type NodeFileRef = ShareRef | DirectoryRef | FileRef;
const fileRefOf = (node: StorageMenuNode): NodeFileRef | undefined => {
  const { resource } = node.metadata;
  return resource.kind === 'share' || resource.kind === 'directory' || resource.kind === 'file' ? resource : undefined;
};
/** The share a node's share-level actions address (the share of a scoped folder / file root; its snapshot kept). */
const shareOf = (node: StorageMenuNode): ShareRef | undefined => {
  const ref = fileRefOf(node);
  return ref ? { kind: 'share', share: ref.share, ...(ref.snapshot ? { snapshot: ref.snapshot } : {}) } : undefined;
};
const connectionOf = (node: StorageMenuNode) => getConnectionById(node.metadata.connectionId).connection;
const isFileFolder = (node: StorageMenuNode): boolean => node.type === 'storage-service-folder' && node.metadata.service === 'file';

/** The refusal of one S7 action on this node (`undefined` = allowed). */
function reasonFor(actionId: string, node: StorageMenuNode): string | undefined {
  const connection = connectionOf(node);
  if (actionId === S7_CREATE_SHARE) return fileActionReason(connection, 'create-share');
  if (actionId === S7_OPEN_SHARE_BY_NAME || actionId === S7_REMOVE_NAMED_SHARE) return undefined;
  const spec = SHARE_ACTIONS[actionId as ShareActionId];
  const ref = fileRefOf(node);
  if (!spec || !ref) return undefined;
  if (spec.dialog === 'sas') {
    const targetProblem = sasTargetProblem(ref);
    if (targetProblem) return targetProblem;
  }
  const op: FileShareOperation = spec.op === 'share-properties' && ref.kind !== 'share' ? 'item-properties' : spec.op;
  return fileActionReason(connection, op, { protocol: node.metadata.protocol ?? 'unknown', snapshot: ref.snapshot, ignoreReadOnly: spec.dialog === 'access-policies' });
}

const DESTRUCTIVE_ACTIONS = new Set<string>(['s7-delete-share', 's7-delete-snapshot']);
const item = (id: string, label: string, icon: string, node: StorageMenuNode): ContextMenuAction => {
  const reason = reasonFor(id, node);
  return { id, label, icon, disabled: !!reason, ...(reason ? { disabledReason: reason } : {}), ...(DESTRUCTIVE_ACTIONS.has(id) ? { destructive: true } : {}) };
};
const SEPARATOR: ContextMenuAction = { id: 'separator', label: '', isSeparator: true };

/** S7 menu factory for S2's registry (`getStorageMenuItems`). */
export function s7MenuItems(node: StorageMenuNode): ContextMenuAction[] {
  if (node.type === 'storage-action') return [];
  if (isFileFolder(node)) return [item(S7_CREATE_SHARE, 'Create share…', 'plus', node), item(S7_OPEN_SHARE_BY_NAME, 'Open share by name…', 'search', node)];
  const ref = fileRefOf(node);
  if (!ref) return [];
  const ids = ref.kind !== 'share' ? SCOPED_ROOT_ITEMS : ref.snapshot ? SNAPSHOT_SHARE_ITEMS : LIVE_SHARE_ITEMS;
  const items = ids.map((id) => (id === null ? SEPARATOR : item(id, SHARE_ACTIONS[id].label, SHARE_ACTIONS[id].icon, node)));
  if (ref.kind === 'share' && !ref.snapshot && node.metadata.shareSource === 'named') items.push(SEPARATOR, item(S7_REMOVE_NAMED_SHARE, 'Remove from this connection', 'delete', node));
  return items;
}

/** The persistent rows under the File Shares folder (S2's `serviceFolderActionRows` seam). */
export const s7ServiceFolderActionRows = (service: StorageService): readonly StorageServiceFolderAction[] =>
  service === 'file' ? [{ id: S7_CREATE_SHARE, label: 'Create share…' }, { id: S7_OPEN_SHARE_BY_NAME, label: 'Open share by name…' }] : [];

export function registerS7StorageTreeExtension(): () => void {
  return registerStorageTreeExtension({ id: 's7-files', slice: 's7', menuItems: s7MenuItems, serviceFolderActionRows: s7ServiceFolderActionRows });
}

const isS7Action = (actionId: string): boolean =>
  actionId === S7_CREATE_SHARE || actionId === S7_OPEN_SHARE_BY_NAME || actionId === S7_REMOVE_NAMED_SHARE || actionId in SHARE_ACTIONS;

/** `true` when the action id is S7's (handled here); an action row clicked while refused shows its reason. */
export function executeS7StorageTreeAction(actionId: string, node: StorageMenuNode): boolean {
  if (!isS7Action(actionId)) return false;
  const reason = reasonFor(actionId, node);
  if (reason) {
    toast.error({ title: 'File share action unavailable', description: reason });
    return true;
  }
  const { connectionId } = node.metadata;
  if (actionId === S7_CREATE_SHARE) openShareDialog({ kind: 'create-share', connectionId, source: 'tree' });
  else if (actionId === S7_OPEN_SHARE_BY_NAME) openShareDialog({ kind: 'open-share-by-name', connectionId, source: 'tree' });
  const ref = fileRefOf(node);
  const share = shareOf(node);
  if (!ref || !share) return true;
  if (actionId === S7_REMOVE_NAMED_SHARE) {
    void removeNamedShare(connectionId, share.share, 'tree');
    return true;
  }
  const { dialog } = SHARE_ACTIONS[actionId as ShareActionId];
  // Properties and Get SAS address the node's own ref (a scoped folder / file root); everything else its share.
  const resource = dialog === 'properties' || dialog === 'sas' ? ref : share;
  const { protocol } = node.metadata;   // the listed protocol spares the dialog a share-protocol read
  if (dialog === 'sas' || dialog === 'access-policies') useStorageDialogStore.getState().openDialog({ kind: dialog, connectionId, resource, source: 'tree', ...(protocol ? { protocol } : {}) });
  else openShareDialog({ kind: dialog, connectionId, resource, source: 'tree', ...(protocol ? { protocol } : {}) });
  return true;
}
